import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { useEffect, useCallback, useState, useRef } from "react";
import {
  Bold, Italic, Underline as UIcon, Strikethrough, Heading2, Heading3,
  List, ListOrdered, Link2, Link2Off, AlignLeft, AlignCenter, AlignRight,
  Undo2, Redo2, Quote, Minus, Eraser, Palette,
} from "lucide-react";

const COLOR_SWATCHES = [
  "#0B1330", "#475569", "#94A3B8", "#FF9933", "#D97706",
  "#16A34A", "#0EA5E9", "#DC2626", "#A855F7", "#000000",
];

/**
 * Reusable WYSIWYG editor (TipTap) used everywhere an admin needs to format
 * long-form text — subtitles, blurbs, vision/mission, page bodies, etc.
 *
 * Props:
 *  - value: current HTML string
 *  - onChange(html): fires on every change
 *  - testid: data-testid prefix (toolbar buttons get suffixed)
 *  - minHeight: tailwind min-h class (default: min-h-[140px])
 *  - placeholder: shown when editor is empty (visual only)
 */
export default function RichTextEditor({ value, onChange, testid = "rte", minHeight = "min-h-[140px]", placeholder = "Start typing…" }) {
  const [colorOpen, setColorOpen] = useState(false);
  const colorRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false); };
    if (colorOpen) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [colorOpen]);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-saffron underline" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: `tiptap-content ${minHeight} w-full px-3 py-2.5 text-sm leading-relaxed focus:outline-none`,
        "data-testid": `${testid}-area`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Treat truly empty editor as empty string
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep editor in sync when the parent replaces value externally (e.g. after load)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    // Only update if content actually differs, to avoid losing caret position on every keystroke
    if ((incoming || "<p></p>") !== current) {
      editor.commands.setContent(incoming || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (https://… or /relative)", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return <div className="rounded-xl border border-slate-200 bg-slate-50 h-32 animate-pulse"/>;

  const Btn = ({ cmd, active, icon: Ico, title, tid }) => (
    <button type="button" data-testid={`${testid}-${tid}`} onClick={cmd} title={title}
      className={`h-8 w-8 grid place-items-center rounded-md transition-colors border text-xs ${active ? "bg-navy text-saffron border-navy" : "bg-white text-navy/80 border-slate-200 hover:bg-slate-50 hover:text-navy"}`}>
      <Ico className="h-3.5 w-3.5"/>
    </button>
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <Btn tid="undo" cmd={() => editor.chain().focus().undo().run()} icon={Undo2} title="Undo"/>
        <Btn tid="redo" cmd={() => editor.chain().focus().redo().run()} icon={Redo2} title="Redo"/>
        <span className="h-5 w-px bg-slate-200 mx-1"/>
        <Btn tid="bold" cmd={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} icon={Bold} title="Bold"/>
        <Btn tid="italic" cmd={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} icon={Italic} title="Italic"/>
        <Btn tid="underline" cmd={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} icon={UIcon} title="Underline"/>
        <Btn tid="strike" cmd={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} icon={Strikethrough} title="Strikethrough"/>
        <span className="h-5 w-px bg-slate-200 mx-1"/>
        <Btn tid="h2" cmd={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} icon={Heading2} title="Heading 2"/>
        <Btn tid="h3" cmd={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} icon={Heading3} title="Heading 3"/>
        <Btn tid="quote" cmd={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} icon={Quote} title="Quote"/>
        <span className="h-5 w-px bg-slate-200 mx-1"/>
        <Btn tid="bullet" cmd={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} icon={List} title="Bullet list"/>
        <Btn tid="ordered" cmd={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} icon={ListOrdered} title="Numbered list"/>
        <span className="h-5 w-px bg-slate-200 mx-1"/>
        <Btn tid="align-l" cmd={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} icon={AlignLeft} title="Align left"/>
        <Btn tid="align-c" cmd={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} icon={AlignCenter} title="Align center"/>
        <Btn tid="align-r" cmd={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} icon={AlignRight} title="Align right"/>
        <span className="h-5 w-px bg-slate-200 mx-1"/>
        <Btn tid="link" cmd={addLink} active={editor.isActive("link")} icon={Link2} title="Link"/>
        <Btn tid="unlink" cmd={() => editor.chain().focus().unsetLink().run()} icon={Link2Off} title="Remove link"/>
        <div className="relative" ref={colorRef}>
          <button type="button" data-testid={`${testid}-color`} onClick={() => setColorOpen(v => !v)} title="Text colour"
            className="h-8 w-8 grid place-items-center rounded-md transition-colors border text-xs bg-white text-navy/80 border-slate-200 hover:bg-slate-50 hover:text-navy">
            <Palette className="h-3.5 w-3.5"/>
          </button>
          {colorOpen && (
            <div className="absolute z-50 top-9 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 w-[180px]">
              <div className="grid grid-cols-5 gap-1.5">
                {COLOR_SWATCHES.map(c => (
                  <button key={c} type="button" onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }}
                    title={c} className="h-6 w-6 rounded-md border border-slate-200 hover:scale-110 transition-transform" style={{ background: c }}/>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                <input type="color" data-testid={`${testid}-color-picker`} className="h-7 w-9 cursor-pointer rounded border border-slate-200"
                  onChange={e => editor.chain().focus().setColor(e.target.value).run()}/>
                <button type="button" onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }}
                  className="text-xs font-semibold text-navy hover:text-saffron transition-colors">Clear</button>
              </div>
            </div>
          )}
        </div>
        <Btn tid="hr" cmd={() => editor.chain().focus().setHorizontalRule().run()} icon={Minus} title="Horizontal line"/>
        <Btn tid="clear" cmd={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} icon={Eraser} title="Clear formatting"/>
      </div>
      <div className="bg-white relative">
        {editor.isEmpty && (
          <div className="pointer-events-none absolute top-2.5 left-3 text-sm text-ink-muted/60 select-none">{placeholder}</div>
        )}
        <EditorContent editor={editor}/>
      </div>
    </div>
  );
}
